import { isAuthorized } from 'services';
import { DashboardCardConfig } from '../types';
import { DEFAULT_CARD_CONFIGS } from '../config/cardConfigs';
import { LkHomeDashboard } from 'generated/openApi';

export class CardConfigService {
    private static readonly STORAGE_KEY = 'dashboard-card-configs';

    static initialize(): DashboardCardConfig[] {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (!stored) {
            return DEFAULT_CARD_CONFIGS;
        }

        try {
            const storedConfigs = JSON.parse(stored) as DashboardCardConfig[];

            const storedIds = new Set(storedConfigs.map(c => c.id));
            const newCards = DEFAULT_CARD_CONFIGS.filter(c => !storedIds.has(c.id));

            if (newCards.length > 0) {
                // add new cards and re-save
                const merged = [...storedConfigs, ...newCards];
                this.save(merged);
                return merged;
            }

            return storedConfigs;
        } catch (error) {
            console.error('Failed to parse stored card configs:', error);
            return DEFAULT_CARD_CONFIGS;
        }
    }

    static save(cards: DashboardCardConfig[]): void {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cards));
    }

    static toggleCard(cards: DashboardCardConfig[], cardId: string): DashboardCardConfig[] {
        return cards.map((card) =>
            card.id === cardId ? { ...card, enabled: !card.enabled } : card
        );
    }
    
    //Check if user has dashboard access for card
    private static hasDashboardAccess(
        card: DashboardCardConfig,
        dashboards: LkHomeDashboard[] | undefined
    ): boolean {
        if (!card.dashboardId) {
            return true;
        }
        return dashboards?.some((d) => d.id === card.dashboardId) ?? false;
    }

    //check if user has required permissions for card
    private static hasRequiredPermissions(card: DashboardCardConfig): boolean {
        if (!card.requiredPermissions || card.requiredPermissions.length === 0) {
            return true;
        }
        return card.requiredPermissions.every((req) =>
            isAuthorized(req.permission, req.action)
        );
    }


    //Filter cards by authorization (dashboard access + permissions)
    static filterByAuthorization(
        cards: DashboardCardConfig[],
        dashboards: LkHomeDashboard[] | undefined
    ): DashboardCardConfig[] {
        return cards.filter((card) => {
            const hasDashboard = this.hasDashboardAccess(card, dashboards);
            const hasPermissions = this.hasRequiredPermissions(card);
            return hasDashboard && hasPermissions;
        });
    }

    static getVisibleCards(
        cards: DashboardCardConfig[],
        dashboards: LkHomeDashboard[] | undefined
    ): DashboardCardConfig[] {
        return this.filterByAuthorization(cards, dashboards)
            .filter((card) => card.enabled)
            .sort((a, b) => a.order - b.order);
    }
}